import { Drawer } from 'antd';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CompanyTheme } from '../../../../../api';
import Close from '../../../../../icons/mobility/close.svg';
import Filter from '../../../../../icons/mobility/filter.svg';
import { useThemeComponents } from '../../../../../themes';
import { FilterGroupContainer } from '../../../StandardApplicationEntrypoint/CarListingPage/CarFilter/ui';
import { useCarListingState } from '../CarListingStateProvider';
import CarFilterContent, { Container } from './CarFilterContent';
import PeriodFilter from './PeriodFilter';

const MobileContainer = styled(Container)`
    margin: 0 1rem;

    & .ant-picker {
        width: 100%;
    }
`;

const ButtonContainer = styled.div`
    padding: 0 16px;

    & .ant-btn {
        text-align: center;

        & > svg:first-of-type {
            display: none;
        }
    }
`;

const StyledDrawer = styled(Drawer)`
    & .ant-drawer-header-title {
        & > .ant-drawer-title {
            font-size: 24px;
            font-weight: bold;
        }

        & > button {
            order: 1;
            margin-right: 0;
        }
    }

    & .ant-drawer-body {
        padding-top: 14px;
    }

    & .ant-drawer-footer {
        padding: 20px 24px;
        box-shadow:
            0 -6px 16px -8px rgba(0, 0, 0, 0.08),
            0 -9px 28px 0 rgba(0, 0, 0, 0.05),
            0 -12px 48px 16px rgba(0, 0, 0, 0.03);
    }
`;

const CarFilterOnSmallScreen = () => {
    const { t } = useTranslation(['carList']);
    const { Button, theme } = useThemeComponents();

    const hasInitialized = useRef(false);

    const {
        state: { openFilterDrawer, modelFilter, rangeVehiclePricesFilter, showFilterDrawer },
        actions,
    } = useCarListingState();

    const previousFilter = useRef<{
        modelFilter: string[];
        rangeVehiclePricesFilter: string[];
    }>(null);

    const onOpen = useCallback(() => {
        previousFilter.current = { modelFilter, rangeVehiclePricesFilter };
        actions.setOpenFilterDrawer(true);
    }, [actions, modelFilter, rangeVehiclePricesFilter]);

    const onClose = useCallback(() => {
        actions.setOpenFilterDrawer(false);
        actions.setModelFilter(previousFilter?.current?.modelFilter || []);
        actions.setRangeVehiclePricesFilter(previousFilter?.current?.rangeVehiclePricesFilter || []);
    }, [actions]);

    const onApply = useCallback(() => {
        previousFilter.current = { modelFilter, rangeVehiclePricesFilter };
        actions.setOpenFilterDrawer(false);
    }, [actions, modelFilter, rangeVehiclePricesFilter]);

    useEffect(() => {
        if (!hasInitialized.current) {
            hasInitialized.current = true;
            // do not show filter by default on mobile
            actions.setOpenFilterDrawer(false);
        }
    }, [actions]);

    if (!showFilterDrawer) {
        return null;
    }

    return (
        <>
            <MobileContainer>
                <FilterGroupContainer>
                    <PeriodFilter />
                </FilterGroupContainer>
            </MobileContainer>
            <ButtonContainer>
                <Button onClick={onOpen} porscheFallbackIcon="filter" size="large" type="tertiary" block>
                    {theme !== CompanyTheme.PorscheV3 && <Filter />}
                    {t('carList:filterButton')}
                </Button>
            </ButtonContainer>
            <StyledDrawer
                closeIcon={<Close />}
                footer={
                    <Button onClick={onApply} type="secondary" block>
                        {t('carList:buttons.applyFilter')}
                    </Button>
                }
                height="100%"
                onClose={onClose}
                open={hasInitialized.current && openFilterDrawer}
                placement="top"
                title={t('carList:filterTitle')}
            >
                <CarFilterContent />
            </StyledDrawer>
        </>
    );
};

export default CarFilterOnSmallScreen;
